import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { FaEye, FaEyeSlash, FaUser, FaLock, FaEnvelope, FaIdCard } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { RegisterData } from '../types';
import { toast } from 'react-toastify';

// Convertir iconos a componentes React válidos
const Eye = FaEye as React.ComponentType;
const EyeSlash = FaEyeSlash as React.ComponentType;
const User = FaUser as React.ComponentType;
const Lock = FaLock as React.ComponentType;
const Envelope = FaEnvelope as React.ComponentType;
const IdCard = FaIdCard as React.ComponentType;

interface RegisterFormData extends RegisterData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
  dni?: string; // Opcional
  acceptTerms: boolean;
}

const RegisterContainer = styled.div`
  min-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: linear-gradient(135deg, var(--primary-orange) 0%, var(--primary-blue) 100%);


  @media (max-width: 768px) {
  margin-top: -50px;
}
`;

const RegisterCard = styled.div`
  background: white;
  border-radius: var(--border-radius);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  padding: 3rem;
  width: 100%;
  max-width: 450px;
`;

const Logo = styled.h1`
  text-align: center;
  color: var(--primary-blue);
  margin-bottom: 2rem;
  font-size: 2rem;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const InputGroup = styled.div`
  position: relative;
`;

const InputIcon = styled.div`
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--gray-medium);
  z-index: 1;
`;

const Input = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 12px 12px 40px;
  border: 2px solid ${props => props.hasError ? '#e74c3c' : 'var(--gray-light)'};
  border-radius: var(--border-radius);
  font-size: 1rem;
  transition: var(--transition);
  
  &:focus {
    outline: none;
    border-color: var(--primary-blue);
  }
  
  &::placeholder {
    color: var(--gray-medium);
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--gray-medium);
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: var(--primary-blue);
  }
`;

const ErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 0.9rem;
  margin-top: 0.25rem;
`;

const SubmitButton = styled.button<{ isTermsAccepted?: boolean }>`
  width: 100%;
  padding: 12px;
  background: ${props => props.isTermsAccepted ? 'var(--primary-orange)' : '#cccccc'};
  color: ${props => props.isTermsAccepted ? 'white' : '#888888'};
  border: none;
  border-radius: var(--border-radius);
  font-size: 1.1rem;
  font-weight: 600;
  cursor: ${props => props.isTermsAccepted ? 'pointer' : 'not-allowed'};
  transition: var(--transition);
  opacity: ${props => props.isTermsAccepted ? '1' : '0.6'};
  
  &:hover:not(:disabled) {
    background: ${props => props.isTermsAccepted ? '#e88a02' : '#cccccc'};
  }
  
  &:disabled {
    background: #cccccc;
    color: #888888;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const LinkContainer = styled.div`
  text-align: center;
  margin-top: 1.5rem;
  
  a {
    color: var(--primary-blue);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      color: var(--primary-orange);
    }
  }
`;

const Divider = styled.div`
  text-align: center;
  margin: 1.5rem 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 1px;
    background: var(--gray-light);
  }
  
  span {
    background: white;
    padding: 0 1rem;
    color: var(--gray-medium);
    position: relative;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin: 1rem 0;
`;

const CheckboxInput = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: var(--primary-blue);
`;

const CheckboxLabel = styled.label<{ hasError?: boolean }>`
  font-size: 0.9rem;
  color: ${props => props.hasError ? '#e74c3c' : 'var(--gray-dark)'};
  line-height: 1.4;
  cursor: pointer;
  
  .terms-link {
    color: var(--primary-blue);
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      color: var(--primary-orange);
    }
  }
`;



const Register: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { register: registerUser, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();
  
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<RegisterFormData>();

  const password = watch('password');
  const acceptTerms = watch('acceptTerms');

  const onSubmit = async (data: RegisterFormData) => {
    try {
      clearError();
      const { confirmPassword, acceptTerms, ...registerData } = data;
      
      await registerUser(registerData); // Ya tiene phone directamente
      toast.success('¡Cuenta creada exitosamente!');
      navigate('/');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error al registrar usuario');
    }
  };

  return (
    <RegisterContainer>
      <RegisterCard>
        <Logo>Crear Cuenta</Logo>
        
        <Form onSubmit={handleSubmit(onSubmit)}>
          <InputGroup>
            <InputIcon>
              <User />
            </InputIcon>
            <Input
              type="text"
              placeholder="Nombre completo"
              hasError={!!errors.name}
              {...register('name', {
                required: 'El nombre es requerido',
                minLength: {
                  value: 2,
                  message: 'El nombre debe tener al menos 2 caracteres'
                }
              })}
            />
            {errors.name && <ErrorMessage>{errors.name.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <IdCard />
            </InputIcon>
            <Input
              type="text"
              placeholder="DNI"
              hasError={!!errors.dni}
              {...register('dni', {
                required: 'El DNI es requerido',
                pattern: {
                  value: /^[0-9]{8}$/,
                  message: 'El DNI debe tener 8 dígitos'
                }
              })}
            />
            {errors.dni && <ErrorMessage>{errors.dni.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <Envelope />
            </InputIcon>
            <Input
              type="email"
              placeholder="Correo electrónico"
              hasError={!!errors.email}
              {...register('email', {
                required: 'El correo es requerido',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Correo electrónico inválido'
                }
              })}
            />
            {errors.email && <ErrorMessage>{errors.email.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <Lock />
            </InputIcon>
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Contraseña"
              hasError={!!errors.password}
              {...register('password', {
                required: 'La contraseña es requerida',
                minLength: {
                  value: 6,
                  message: 'La contraseña debe tener al menos 6 caracteres'
                }
              })}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeSlash /> : <Eye />}
            </PasswordToggle>
            {errors.password && <ErrorMessage>{errors.password.message}</ErrorMessage>}
          </InputGroup>

          <InputGroup>
            <InputIcon>
              <Lock />
            </InputIcon>
            <Input
              type={showConfirmPassword ? 'text' : 'password'}
              placeholder="Confirmar contraseña"
              hasError={!!errors.confirmPassword}
              {...register('confirmPassword', {
                required: 'Confirma tu contraseña',
                validate: value => value === password || 'Las contraseñas no coinciden'
              })}
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            >
              {showConfirmPassword ? <EyeSlash /> : <Eye />}
            </PasswordToggle>
            {errors.confirmPassword && <ErrorMessage>{errors.confirmPassword.message}</ErrorMessage>}
          </InputGroup>

          <CheckboxContainer>
            <CheckboxInput
              type="checkbox"
              id="acceptTerms"
              {...register('acceptTerms', {
                required: 'Debes aceptar los términos y condiciones'
              })}
            />
            <CheckboxLabel htmlFor="acceptTerms" hasError={!!errors.acceptTerms}>
              Acepto los{' '}
              <Link to="/terms" className="terms-link">
                términos y condiciones
              </Link>
            </CheckboxLabel>
          </CheckboxContainer>
          {errors.acceptTerms && <ErrorMessage>{errors.acceptTerms.message}</ErrorMessage>}
          
          <SubmitButton 
            type="submit" 
            disabled={isLoading || !acceptTerms}
            isTermsAccepted={acceptTerms}
          >
            {isLoading ? 'Creando cuenta...' : 'Crear Cuenta'}
          </SubmitButton>
        </Form>

        <Divider>
          <span>¿Ya tienes cuenta?</span>
        </Divider>

        <LinkContainer>
          <Link to="/login">Iniciar sesión</Link>
        </LinkContainer>
      </RegisterCard>

    </RegisterContainer>
  );
};

export default Register;

// Mantener el campo dni existente y agregar phone


interface RegisterFormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;  // Cambiar de dni a phone
}